import React, { useState, useEffect, useRef } from "react";
import Select from 'react-select';

import {
  Edit,
  Delete,
  Settings,
  XCircle,
  Save,
  Users,
  BookOpen,
  GraduationCap,
  Plus,
  X,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

// Simple toast implementation since react-hot-toast isn't available
const toast = {
  success: (message) => {
    const div = document.createElement("div");
    div.className =
      "fixed top-4 right-4 bg-green-500 text-white px-4 py-2 rounded shadow-lg z-50";
    div.textContent = message;
    document.body.appendChild(div);
    setTimeout(() => document.body.removeChild(div), 3000);
  },
  error: (message) => {
    const div = document.createElement("div");
    div.className =
      "fixed top-4 right-4 bg-red-500 text-white px-4 py-2 rounded shadow-lg z-50";
    div.textContent = message;
    document.body.appendChild(div);
    setTimeout(() => document.body.removeChild(div), 3000);
  },
};

// Dropdown component for universities and residencies

let openDropdownId = null; // keeps track of which dropdown is open globally

function DropdownList({ items, type, id }) {
  const [isOpen, setIsOpen] = useState(false);

  const toggleDropdown = () => {
    if (openDropdownId === id) {
      openDropdownId = null;
      setIsOpen(false);
    } else {
      openDropdownId = id;
      setIsOpen(true);
    }
  };

  if (!items || items.length === 0) {
    return <span className="text-gray-400">-</span>;
  }

  return (
    <div className="relative inline-block">
      <span
        onClick={toggleDropdown}
        className="text-blue-600 text-sm cursor-pointer hover:underline flex items-center gap-1"
      >
        Click to view all
        {isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </span>

      {isOpen && (
        <div className="absolute top-full left-0 mt-2 w-64 max-h-48 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg z-10 p-3">
          <div className="flex justify-between items-center mb-2">
            <h4 className="font-medium text-sm text-gray-700">
              {type === "university" ? "Universities" : "Residencies"}
            </h4>
            <button
              onClick={toggleDropdown}
              className="text-gray-400 hover:text-gray-600"
            >
              <X size={14} />
            </button>
          </div>

          <div className="space-y-1">
            {items.map((item, index) => (
              <div
                key={index}
                className={`px-2 py-1 text-xs rounded ${
                  type === "university"
                    ? "bg-blue-50 text-blue-700"
                    : "bg-purple-50 text-purple-700"
                }`}
              >
                {item}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ProgrammeRow({
  item,
  index,
  startIndex,
  setDeleteConfirm,
  setEditItem,
  updateStatus,
  workingId,
}) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    }
    if (dropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownOpen]);

  // Parse universities and residencies from the item data
  const universities = item.university_names 
    ? item.university_names.split(', ').filter(Boolean)
    : [];

  const residencies = item.residencies
    ? item.residencies.split(', ').filter(Boolean)
    : [];

  return (
    <tr key={`${item.id}-${index}`} className="hover:bg-gray-50 transition border-b border-gray-100">
      <td className="px-4 py-3 text-center text-sm font-medium text-gray-900">
        {startIndex + index + 1}
      </td>
      <td className="px-4 py-3">
        <div className="max-w-xs">
          <span className="font-semibold text-gray-900 text-sm line-clamp-2">
            {item.programme}
          </span>
          {item.entry_level && (
            <span className="block text-xs text-gray-500 mt-1">
              {item.entry_level}
            </span>
          )}
        </div>
      </td>
      <td className="px-4 py-3">
        <DropdownList 
          items={universities} 
          type="university" 
          maxVisible={1}
        />
      </td>
      <td className="px-4 py-3">
        <div className="max-w-xs">
          {item.group_names ? (
            <span className="inline-flex flex-wrap gap-1">
              {item.group_names.split(', ').map((group, idx) => (
                <span
                  key={idx}
                  className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full border border-green-200"
                >
                  {group}
                </span>
              ))}
            </span>
          ) : (
            <span className="text-gray-400">-</span>
          )}
        </div>
      </td>
      <td className="px-4 py-3">
        <span className="text-sm text-gray-700">
          {item.programme_group_names || "-"}
        </span>
      </td>
      <td className="px-4 py-3">
        <span className="text-sm text-gray-700">
          {item.city_names || "-"}
        </span>
      </td>
      <td className="px-4 py-3">
        <DropdownList 
          items={residencies} 
          type="residency" 
          maxVisible={1}
        />
      </td>
      <td className="px-4 py-3 text-center">
        <select
          value={item.is_active ? "Active" : "Inactive"}
          onChange={(e) => updateStatus(item.id, e.target.value === "Active")}
          disabled={workingId === item.id}
          className={`px-3 py-1 rounded-full text-xs font-semibold border focus:outline-none focus:ring-2 focus:ring-offset-1 transition-all ${
            item.is_active
              ? "text-green-800 bg-green-100 border-green-400 focus:ring-green-400"
              : "text-red-800 bg-red-100 border-red-400 focus:ring-red-400"
          } ${workingId === item.id ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        >
          <option value="Active">Active</option>
          <option value="Inactive">Inactive</option>
        </select>
      </td>
      <td className="relative px-4 py-3 text-center">
        <button
          onClick={() => setDropdownOpen((prev) => !prev)}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors border border-transparent hover:border-gray-300"
          aria-haspopup="true"
          aria-expanded={dropdownOpen}
          aria-label="Toggle settings menu"
          type="button"
          disabled={workingId === item.id}
        >
          {workingId === item.id ? (
            <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          ) : (
            <Settings size={16} className="text-gray-600" />
          )}
        </button>

        {dropdownOpen && (
          <div
            ref={dropdownRef}
            className="absolute right-0 mt-1 w-32 bg-white border border-gray-200 rounded-lg shadow-lg z-10 overflow-hidden"
            role="menu"
          >
            <button
              onClick={() => {
                setEditItem(item);
                setDropdownOpen(false);
              }}
              className="w-full text-left px-4 py-2 hover:bg-blue-50 text-blue-600 transition-colors flex items-center gap-2"
              type="button"
            >
              <Edit size={14} />
              <span className="text-sm">Edit</span>
            </button>
            <button
              onClick={() => {
                setDeleteConfirm(item);
                setDropdownOpen(false);
              }}
              className="w-full text-left px-4 py-2 hover:bg-red-50 text-red-600 transition-colors flex items-center gap-2"
              type="button"
            >
              <Delete size={14} />
              <span className="text-sm">Delete</span>
            </button>
          </div>
        )}
      </td>
    </tr>
  );
}

function EditProgrammeModal({ editItem, setEditItem, onSave }) {
  const [formData, setFormData] = useState({
    programmeName: "",
    programme_group_id: "",
    group_id: "",
    entryLevel: "",
    intakes: [""],
    deliveryPatterns: [],
    residency_ids: [],
    university_ids: [], // Added university_ids field
    status: true,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [errors, setErrors] = useState({});

  // dropdown master data
  const [programmeGroups, setProgrammeGroups] = useState([]);
  const [groups, setGroups] = useState([]);
  const [deliveryPatterns, setDeliveryPatterns] = useState([]);
  const [residencies, setResidencies] = useState([]);
  const [universities, setUniversities] = useState([]);

  // 🔹 fetch universities
  const fetchUniversities = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/catalog/all-universities`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch universities");
      }

      const data = await response.json();
      const formattedUniversities = data.map(uni => ({
        value: uni.id,
        label: uni.university
      }));
      setUniversities(formattedUniversities);
    } catch (error) {
      console.error("Error fetching universities:", error);
      toast.error("Failed to load university options");
    }
  };

  // 🔹 fetch residencies
  const fetchResidencies = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/catalog/residency-status`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch residencies");
      }

      const data = await response.json();
      const formattedResidencies = data
        .filter(res => res.status === 1)
        .map(res => ({
          value: res.id,
          label: res.residency
        }));
      setResidencies(formattedResidencies);
    } catch (error) {
      console.error("Error fetching residencies:", error);
      toast.error("Failed to load residency options");
    }
  };

  // 🔹 helpers for intake conversion
  const formatIntakeFromApi = (intake) => {
    const [month, year] = intake.split("/");
    return `20${year}-${month.padStart(2, "0")}`;
  };

  const formatIntakeForApi = (intake) => {
    const [year, month] = intake.split("-");
    return `${month}/${year.slice(-2)}`;
  };

  // 🔹 fetch programme details
  useEffect(() => {
    if (editItem?.id) {
      fetchProgrammeDetails(editItem.id);
      fetchResidencies();
      fetchUniversities(); // Fetch universities when modal opens
    }
  }, [editItem?.id]);

  const fetchProgrammeDetails = async (programmeId) => {
    setLoadingDetails(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/catalog/get-programme/${programmeId}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch programme details");
      }

      const data = await response.json();

      // Set dropdown data
      setProgrammeGroups(data.programmeGroups || []);
      setGroups(data.allGroups || []);
      setDeliveryPatterns(data.allDeliveryPatterns || []);
      
      // Note: We're now fetching universities separately via fetchUniversities()
      // So we don't setUniversities here anymore

      // Auto-fill form with programme details
      setFormData({
        programmeName: data.programmeName || "",
        programme_group_id: data.programmeGroupId?.toString() || "",
        group_id: data.groups?.[0]?.id?.toString() || "",
        entryLevel: data.entryLevel || "",
        intakes: data.intakes?.length
          ? data.intakes.map(formatIntakeFromApi)
          : [""],
        deliveryPatterns: data.deliveryPatterns?.map((dp) => dp.id) || [],
        residency_ids: data.residencies?.map((res) => res.id) || [],
        university_ids: data.universities?.map((uni) => uni.id) || [], // Set university IDs from API
        status: data.status !== undefined ? Boolean(data.status) : true,
      });
    } catch (error) {
      console.error("Error fetching programme details:", error);
      toast.error("Failed to load programme details");
    } finally {
      setLoadingDetails(false);
    }
  };

  // 🔹 form validation
  const validateForm = () => {
    const newErrors = {};
    if (!formData.programmeName.trim())
      newErrors.programmeName = "Programme name is required";
    if (!formData.programme_group_id)
      newErrors.programme_group_id = "Programme group is required";
    if (!formData.group_id) newErrors.group_id = "Group is required";
    if (!formData.entryLevel.trim())
      newErrors.entryLevel = "Entry level is required";
    if (!formData.intakes.filter((i) => i.trim()).length)
      newErrors.intakes = "At least one intake is required";
    if (!formData.deliveryPatterns.length)
      newErrors.deliveryPatterns = "At least one delivery pattern is required";
    if (!formData.university_ids.length) // Added validation for universities
      newErrors.university_ids = "At least one university is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // 🔹 submit handler
  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error("Please fix validation errors");
      return;
    }
    setIsLoading(true);
    try {
      const dataToSend = {
        programme_group_id: parseInt(formData.programme_group_id, 10),
        group_id: parseInt(formData.group_id, 10),
        programmeName: formData.programmeName.trim(),
        deliveryPatterns: formData.deliveryPatterns.map((dp) =>
          parseInt(dp, 10)
        ),
        entryLevel: formData.entryLevel.trim(),
        intakes: formData.intakes
          .filter((i) => i.trim())
          .map(formatIntakeForApi),
        residency_ids: formData.residency_ids,
        university_ids: formData.university_ids, // Added university_ids
        status: formData.status,
        updatedBy: 1,
      };

      await onSave(editItem.id, dataToSend);
      setEditItem(null);
      toast.success("Programme updated successfully!");
    } catch (error) {
      console.error("Error updating programme:", error);
      toast.error("Failed to update programme");
    } finally {
      setIsLoading(false);
    }
  };

  // 🔹 generic input change
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  // 🔹 handle residency selection change
  const handleResidencyChange = (selectedOptions) => {
    const selectedIds = selectedOptions ? selectedOptions.map(option => option.value) : [];
    setFormData(prev => ({ ...prev, residency_ids: selectedIds }));
  };

  // 🔹 handle university selection change
  const handleUniversityChange = (selectedOptions) => {
    const selectedIds = selectedOptions ? selectedOptions.map(option => option.value) : [];
    setFormData(prev => ({ ...prev, university_ids: selectedIds }));
    if (errors.university_ids) {
      setErrors((prev) => ({ ...prev, university_ids: "" }));
    }
  };

  // 🔹 get selected residencies for react-select
  const getSelectedResidencies = () => {
    return residencies.filter(res => 
      formData.residency_ids.includes(res.value)
    );
  };

  // 🔹 get selected universities for react-select
  const getSelectedUniversities = () => {
    return universities.filter(uni => 
      formData.university_ids.includes(uni.value)
    );
  };

  // 🔹 intakes management
  const addIntake = () =>
    setFormData((p) => ({ ...p, intakes: [...p.intakes, ""] }));
  const removeIntake = (i) =>
    setFormData((p) => ({
      ...p,
      intakes: p.intakes.filter((_, idx) => idx !== i),
    }));
  const updateIntake = (i, val) =>
    setFormData((p) => ({
      ...p,
      intakes: p.intakes.map((x, idx) => (idx === i ? val : x)),
    }));

  // 🔹 delivery pattern checkbox
  const handleDeliveryPatternChange = (id, checked) => {
    setFormData((p) => ({
      ...p,
      deliveryPatterns: checked
        ? [...p.deliveryPatterns, id]
        : p.deliveryPatterns.filter((x) => x !== id),
    }));
  };

  if (!editItem) return null;
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="bg-gradient-to-r from-blue-600 to-cyan-600 px-4 py-2 rounded-t-2xl flex items-center justify-between shadow-md">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white bg-opacity-20 rounded-lg">
                <Edit size={20} className="text-white" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white">
                  Edit Programme
                </h3>
                <p className="text-blue-100 text-sm">
                  Update programme information
                </p>
              </div>
            </div>
            <button
              onClick={() => setEditItem(null)}
              className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
            >
              <XCircle size={20} className="text-white" />
            </button>
          </div>
        </div>

        <div className="p-6">
          {loadingDetails ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
              <span className="ml-3 text-gray-600">
                Loading programme details...
              </span>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Programme Information Card */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
                  <BookOpen size={16} />
                  Programme Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Programme Name *
                    </label>
                    <input
                      type="text"
                      value={formData.programmeName}
                      onChange={(e) =>
                        handleInputChange("programmeName", e.target.value)
                      }
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                        errors.programmeName
                          ? "border-red-500 bg-red-50"
                          : "border-gray-300"
                      }`}
                      placeholder="Enter programme name"
                    />
                    {errors.programmeName && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.programmeName}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Entry Level *
                    </label>
                    <input
                      type="text"
                      value={formData.entryLevel}
                      onChange={(e) =>
                        handleInputChange("entryLevel", e.target.value)
                      }
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                        errors.entryLevel
                          ? "border-red-500 bg-red-50"
                          : "border-gray-300"
                      }`}
                      placeholder="e.g., Bachelor's, Master's"
                    />
                    {errors.entryLevel && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.entryLevel}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Universities Selection Card */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-700 mb-2 flex items-center gap-2">
                  <GraduationCap size={16} />
                  Associated Universities *
                </h4>
                <Select
                  isMulti
                  options={universities}
                  value={getSelectedUniversities()}
                  onChange={handleUniversityChange}
                  className="react-select-container"
                  classNamePrefix="react-select"
                  placeholder="Select universities..."
                  noOptionsMessage={() => "No universities available"}
                />
                {errors.university_ids && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.university_ids}
                  </p>
                )}
              </div>

              {/* Programme Structure Card */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="font-semibold text-green-800 mb-2 flex items-center gap-2">
                  <Settings size={16} />
                  Programme Structure
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Programme Group *
                    </label>
                    <select
                      value={formData.programme_group_id}
                      onChange={(e) =>
                        handleInputChange("programme_group_id", e.target.value)
                      }
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                        errors.programme_group_id
                          ? "border-red-500 bg-red-50"
                          : "border-gray-300"
                      }`}
                    >
                      <option value="">Select Programme Group</option>
                      {programmeGroups?.map((pg) => (
                        <option key={pg.id} value={pg.id}>
                          {pg.name || pg.programme_group_name}
                        </option>
                      ))}
                    </select>
                    {errors.programme_group_id && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.programme_group_id}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Group *
                    </label>
                    <select
                      value={formData.group_id}
                      onChange={(e) =>
                        handleInputChange("group_id", e.target.value)
                      }
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                        errors.group_id
                          ? "border-red-500 bg-red-50"
                          : "border-gray-300"
                      }`}
                    >
                      <option value="">Select Group</option>
                      {groups?.map((group) => (
                        <option key={group.id} value={group.id}>
                          {group.group_name}
                        </option>
                      ))}
                    </select>
                    {errors.group_id && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.group_id}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Residency & Delivery Card */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Residency Status */}
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <h4 className="font-semibold text-purple-800 mb-2">
                    Residency Status
                  </h4>
                  <Select
                    isMulti
                    options={residencies}
                    value={getSelectedResidencies()}
                    onChange={handleResidencyChange}
                    className="react-select-container"
                    classNamePrefix="react-select"
                    placeholder="Select residency statuses..."
                    noOptionsMessage={() => "No residency options available"}
                  />
                </div>

                {/* Delivery Patterns */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-700 mb-2">
                    Delivery Patterns *
                  </h4>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {deliveryPatterns?.map((pattern) => (
                      <label
                        key={pattern.id}
                        className="flex items-center gap-3 p-2 hover:bg-blue-100 rounded cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={formData.deliveryPatterns.includes(pattern.id)}
                          onChange={(e) =>
                            handleDeliveryPatternChange(
                              pattern.id,
                              e.target.checked
                            )
                          }
                          className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">
                          {pattern.pattern_name || pattern.pattern}
                        </span>
                      </label>
                    ))}
                  </div>
                  {errors.deliveryPatterns && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.deliveryPatterns}
                    </p>
                  )}
                </div>
              </div>

              {/* Intakes Card */}
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <h4 className="font-semibold text-orange-800 mb-2 flex items-center gap-2">
                  <Users size={16} />
                  Intakes *
                </h4>
                <div className="space-y-3">
                  {formData.intakes.map((intake, index) => (
                    <div key={index} className="flex gap-3">
                      <input
                        type="month"
                        value={intake}
                        onChange={(e) => updateIntake(index, e.target.value)}
                        className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      />
                      {formData.intakes.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeIntake(index)}
                          className="px-3 py-3 text-red-500 hover:bg-red-50 rounded-lg border border-red-200 transition-colors"
                        >
                          <X size={16} />
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addIntake}
                    className="flex items-center gap-2 px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg border border-blue-200 transition-colors"
                  >
                    <Plus size={16} />
                    Add Another Intake
                  </button>
                </div>
                {errors.intakes && (
                  <p className="mt-1 text-sm text-red-600">{errors.intakes}</p>
                )}
              </div>

              {/* Status Card */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-800 mb-2">
                  Programme Status
                </h4>
                <select
                  value={formData.status ? "Active" : "Inactive"}
                  onChange={(e) =>
                    handleInputChange("status", e.target.value === "Active")
                  }
                  className="w-full md:w-48 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={() => setEditItem(null)}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              disabled={isLoading || loadingDetails}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={isLoading || loadingDetails}
              className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-200 font-medium shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Updating...
                </>
              ) : (
                <>
                  <Save size={16} />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function DeleteConfirmModal({
  deleteConfirm,
  setDeleteConfirm,
  onDelete,
  isDeleting,
}) {
  if (!deleteConfirm) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
        <div className="bg-gradient-to-r from-red-500 to-red-600 px-6 py-4 rounded-t-xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white bg-opacity-20 rounded-lg">
                <Delete size={20} className="text-white" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white">
                  Delete Programme
                </h3>
                <p className="text-red-100 text-sm">
                  This action cannot be undone
                </p>
              </div>
            </div>
            <button
              onClick={() => setDeleteConfirm(null)}
              className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
              disabled={isDeleting}
            >
              <XCircle size={20} className="text-white" />
            </button>
          </div>
        </div>

        <div className="p-6">
          <div className="mb-6">
            <p className="text-gray-600 mb-2">
              Are you sure you want to delete the following programme?
            </p>
            <div className="bg-gray-50 p-4 rounded-lg border">
              <p className="font-semibold text-gray-800">
                {deleteConfirm.programme}
              </p>
              <p className="text-sm text-gray-600">
                {deleteConfirm.university_name}
              </p>
            </div>
            <p className="text-sm text-red-600 mt-3">
              This will also delete all related university programme
              associations.
            </p>
          </div>

          <div className="flex justify-end gap-3">
            <button
              onClick={() => setDeleteConfirm(null)}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              disabled={isDeleting}
            >
              Cancel
            </button>
            <button
              onClick={() => onDelete(deleteConfirm.id)}
              disabled={isDeleting}
              className="px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg hover:from-red-600 hover:to-red-700 transition-all duration-200 font-medium shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isDeleting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Deleting...
                </>
              ) : (
                <>
                  <Delete size={16} />
                  Delete Programme
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AllProgrammes({ theme }) {
  const [programmeData, setProgrammeData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [entriesToShow, setEntriesToShow] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [workingId, setWorkingId] = useState(null);
  const [programmeGroups, setProgrammeGroups] = useState([]);
  const [groups, setGroups] = useState([]);
  const [cities, setCities] = useState([]);
  const [universities, setUniversities] = useState([]);
  const [deliveryPatterns, setDeliveryPatterns] = useState([]);
  const [error, setError] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const getPageNumbers = (total, current, maxButtons = 5) => {
    const half = Math.floor(maxButtons / 2);
    let start = Math.max(1, current - half);
    let end = Math.min(total, start + maxButtons - 1);
    if (end - start + 1 < maxButtons) {
      start = Math.max(1, end - maxButtons + 1);
    }
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [
          groupRes,
          programmeGroupRes,
          universitiesRes,
          deliveryPatternsRes,
        ] = await Promise.all([
          fetch(`${process.env.REACT_APP_BACKEND_URL}/catalog/get-ac-groups`, {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }),
          fetch(
            `${process.env.REACT_APP_BACKEND_URL}/catalog/programme-groups`,
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            }
          ),
          fetch(`${process.env.REACT_APP_BACKEND_URL}/catalog/universities`, {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }),
          fetch(
            `${process.env.REACT_APP_BACKEND_URL}/catalog/delivery-patterns`,
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            }
          ),
        ]);

        if (
          !groupRes.ok ||
          !programmeGroupRes.ok ||
          !universitiesRes.ok ||
          !deliveryPatternsRes.ok
        ) {
          throw new Error("Failed to fetch dropdown data");
        }

        const [
          groupData,
          programmeGroupData,
          universitiesData,
          deliveryPatternsData,
        ] = await Promise.all([
          groupRes.json(),
          programmeGroupRes.json(),
          universitiesRes.json(),
          deliveryPatternsRes.json(),
        ]);

        setGroups(groupData);
        setProgrammeGroups(programmeGroupData);
        setUniversities(universitiesData);
        setDeliveryPatterns(deliveryPatternsData);
        setError(null);
      } catch (err) {
        console.error("Error fetching dropdown data:", err);
        setError("Failed to load dropdown options");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const fetchProgrammes = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/catalog/programmes`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch programmes");
      }

      const data = await response.json();
      setProgrammeData(data);
    } catch (err) {
      console.error("Error fetching programmes:", err);
      toast.error("Failed to fetch programmes.");
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, isActive) => {
    setWorkingId(id);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/catalog/update-programme-status/${id}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({ 
            status: isActive ? 1 : 0
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to update status");
      }

      setProgrammeData((prev) =>
        prev.map((prog) =>
          prog.id === id ? { ...prog, is_active: isActive } : prog
        )
      );

      toast.success("Status updated successfully!");
    } catch (error) {
      console.error("Status update error:", error);
      toast.error(error.message || "Failed to update status");
    } finally {
      setWorkingId(null);
    }
  };

  const updateProgramme = async (id, formData) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/catalog/update-programme/${id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify(formData),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to update programme");
      }

      await fetchProgrammes();
      toast.success("Programme updated successfully.");
    } catch (error) {
      console.error("Update error:", error);
      toast.error(error.message || "Failed to update programme.");
      throw error;
    }
  };

  const deleteProgramme = async (id) => {
    setIsDeleting(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/catalog/delete-programme/${id}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        toast.error(errorData.message || "Failed to delete programme.");
        return;
      }

      setProgrammeData((prev) => prev.filter((item) => item.id !== id));
      toast.success("Programme deleted successfully.");
      setDeleteConfirm(null);
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Failed to delete programme. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  };

  useEffect(() => {
    fetchProgrammes();
  }, []);

  const filteredData = programmeData.filter(
    (item) =>
      item.programme?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.university_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.city_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.group_names?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.programme_group_names?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalItems = filteredData.length;
  const totalPages =
    entriesToShow === "All" ? 1 : Math.ceil(totalItems / entriesToShow);

  useEffect(() => {
    setCurrentPage(1);
  }, [entriesToShow, searchTerm]);

  const startIndex =
    (currentPage - 1) * (entriesToShow === "All" ? totalItems : entriesToShow);
  const endIndex = Math.min(
    startIndex + (entriesToShow === "All" ? totalItems : entriesToShow),
    totalItems
  );
  const displayedData =
    entriesToShow === "All"
      ? filteredData
      : filteredData.slice(startIndex, endIndex);

  const goToPage = (page) => {
    if (page < 1) page = 1;
    if (page > totalPages) page = totalPages;
    setCurrentPage(page);
  };

  const isModalOpen = editItem || deleteConfirm;

  if (loading && programmeData.length === 0) {
    return (
      <div className="w-full flex items-center justify-center p-10">
        <div className="flex items-center gap-3">
          <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-600 text-lg">Loading programmes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-gray-50">
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-20 backdrop-blur-sm z-40"></div>
      )}

      <div
        className={`max-w-7xl mx-auto mt-6 bg-white rounded-xl shadow-lg p-6 transition-all duration-300 ${
          isModalOpen ? "relative z-30" : ""
        }`}
      >
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              All Programmes
            </h2>
            <p className="text-gray-600 mt-1">
              Manage and view all programme information
            </p>
          </div>
          
          <div className="flex items-center gap-4 mt-4 lg:mt-0">
            <div className="relative flex-1 lg:flex-none">
              <input
                type="text"
                placeholder="Search programmes, universities, cities..."
                className="w-full lg:w-80 px-4 py-2 pl-10 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <label
                htmlFor="entriesToShow"
                className="text-sm font-medium text-gray-700 whitespace-nowrap"
              >
                Show:
              </label>
              <select
                id="entriesToShow"
                value={entriesToShow}
                onChange={(e) =>
                  setEntriesToShow(
                    e.target.value === "All" ? "All" : Number(e.target.value)
                  )
                }
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              >
                {[10, 25, 50, "All"].map((num) => (
                  <option key={num} value={num}>
                    {num}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    #
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Programme
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Universities
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Groups
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Programme Groups
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    City
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Residencies
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {displayedData.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-4 py-8 text-center">
                      <div className="flex flex-col items-center justify-center text-gray-500">
                        <BookOpen size={48} className="text-gray-300 mb-3" />
                        <p className="text-lg font-medium mb-1">No programmes found</p>
                        <p className="text-sm">
                          {searchTerm ? "Try adjusting your search terms" : "No programmes available"}
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  displayedData.map((item, index) => (
                    <ProgrammeRow
                      key={item.id}
                      item={item}
                      index={index}
                      startIndex={startIndex}
                      setDeleteConfirm={setDeleteConfirm}
                      setEditItem={setEditItem}
                      updateStatus={updateStatus}
                      workingId={workingId}
                    />
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {entriesToShow !== "All" && totalPages > 1 && (
          <div className="flex flex-col sm:flex-row items-center justify-between mt-6 px-4 py-3 bg-gray-50 rounded-lg border border-gray-200">
            <div className="text-sm text-gray-700 mb-4 sm:mb-0">
              Showing {startIndex + 1} to {Math.min(endIndex, totalItems)} of {totalItems} entries
            </div>
            
            <nav className="flex items-center space-x-1">
              <button
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
                className={`px-3 py-1 rounded-md ${
                  currentPage === 1
                    ? "text-gray-400 cursor-not-allowed"
                    : "text-gray-700 hover:bg-gray-200"
                }`}
              >
                Previous
              </button>
              
              {getPageNumbers(totalPages, currentPage).map((pageNum) => (
                <button
                  key={pageNum}
                  onClick={() => goToPage(pageNum)}
                  className={`px-3 py-1 rounded-md ${
                    pageNum === currentPage
                      ? "bg-blue-600 text-white"
                      : "text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {pageNum}
                </button>
              ))}
              
              <button
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`px-3 py-1 rounded-md ${
                  currentPage === totalPages
                    ? "text-gray-400 cursor-not-allowed"
                    : "text-gray-700 hover:bg-gray-200"
                }`}
              >
                Next
              </button>
            </nav>
          </div>
        )}
      </div>

      <EditProgrammeModal
        editItem={editItem}
        setEditItem={setEditItem}
        onSave={updateProgramme}
      />

      <DeleteConfirmModal
        deleteConfirm={deleteConfirm}
        setDeleteConfirm={setDeleteConfirm}
        onDelete={deleteProgramme}
        isDeleting={isDeleting}
      />
    </div>
  );
}